import RejectedStageDialog from "../job-details/RejectedStageDialog";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import InterviewScheduleInline from "../InterviewScheduleInline";
import RecruitingStageAccordionList from "../job-details/RecruitingStageAccordionList";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Divider,
  Button,
  Checkbox,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Link as MuiLink,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Building, Location, ProfileCircle, Clock } from "iconsax-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { AccordionHeadingIcon } from "../job-details/StageViewAccordionList";
import { MoveStageActionIcon } from "../ActionButtonIcons";
import { MOVE_STAGE_BULK_BUTTON_SX } from "../stageBulkButtonStyles";
import { buildOpenCompanyNavPayload } from "../../utils/companyNavigation";

const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const TEXT = "#111827";
const MUTED = "#94A3B8";
const BG = "#FFFFFF";
const API_BASE = (process.env.REACT_APP_API_BASE || "").trim();
const NAV_CACHE_KEY = "rpo:openCompanyNav";
const STAGE_ORDER = [
  "New",
  "Applied",
  "Matched",
  "Sourced",
  "Contacted",
  "Screened",
  "Presented",
  "Interview",
  "Assessment",
  "Offer",
  "Hired",
  "Rejected",
];
const MOVE_STAGE_OPTIONS = [
  "New",
  "Applied",
  "Matched",
  "Sourced",
  "Contacted",
  "Screened",
  "Presented",
  "Interview",
  "Assessment",
  "Offer",
  "Hired",
  "Rejected",
];

function buildApiUrl(pathname) {
  const p = pathname?.startsWith("/") ? pathname : `/${pathname || ""}`;
  return API_BASE ? `${API_BASE}${p}` : p;
}

function toPositiveInt(value) {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function normalizeStageLabel(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "all") return "All Stages";
  if (raw === "new") return "New";
  if (raw === "applied") return "Applied";
  if (raw === "matched") return "Matched";
  if (raw === "sourced") return "Sourced";
  if (raw === "contacted") return "Contacted";
  if (raw === "screened") return "Screened";
  if (raw === "assessment" || raw === "assessments") return "Assessment";
  if (raw === "offer") return "Offer";
  if (raw === "hired") return "Hired";
  if (raw === "rejected") return "Rejected";
  if (raw === "interviewing" || raw === "interview") return "Interview";
  if (raw === "presented") return "Presented";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function resolveStageItemLabel(row = {}) {
  const explicitStage = String(
    row?.candidate_stage || row?.stage || row?.candidate_status || "New"
  ).trim();

  return normalizeStageLabel(explicitStage) || "New";
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

function getAuthHeaders() {
  const token = getToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(token ? { "x-access-token": token } : {}),
  };
}

function getStageItemKey(row = {}) {
  const explicitKey = String(row?.itemKey || "").trim();
  if (explicitKey) return explicitKey;

  const numericJobId = Number(row?.job_id || row?.jobId || 0);
  const numericCandidateId = Number(row?.candidate_id || row?.candidateId || 0);
  if (
    Number.isFinite(numericJobId) &&
    numericJobId > 0 &&
    Number.isFinite(numericCandidateId) &&
    numericCandidateId > 0
  ) {
    return `${numericJobId}:${numericCandidateId}`;
  }

  const numericMatchId = Number(row?.id || row?.match_id || 0);
  if (Number.isFinite(numericMatchId) && numericMatchId > 0) {
    return String(numericMatchId);
  }

  return "";
}

function normalizeLookupName(value) {
  return String(value || "").replace(/\s+/g, " ").trim().toLowerCase();
}

function getRecruiterProfileId(recruiterUsersByName, recruiterName) {
  const lookupKey = normalizeLookupName(recruiterName);
  if (!lookupKey || typeof recruiterUsersByName?.get !== "function") return null;

  const matchedRecruiter = recruiterUsersByName.get(lookupKey);
  const numericId = Number(matchedRecruiter?.id || 0);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
}

function mapRowToStageItem(row = {}, recruiterUsersByName = null) {
  const stageLabel = resolveStageItemLabel(row);
  const itemKey = getStageItemKey(row);
  const numericJobId = Number(row?.job_id || row?.jobId || 0);
  const numericCandidateId = Number(row?.candidate_id || row?.candidateId || 0);
  const numericCompanyId = Number(row?.company_id || row?.companyId || 0);
  const recruiterName = String(row?.recruiter_name || row?.owner || "").trim();
  const hiringManagerName = String(
    row?.hiring_manager_name || row?.contact_name || row?.primary_contact || ""
  ).trim();
  const companyName = String(row?.company_name || row?.company || "").trim();
  const employment = String(row?.employment || row?.employment_type || "").trim();

  return {
    id:
      itemKey ||
      row?.id ||
      `${row?.job_id || "job"}-${row?.candidate_id || row?.job_title || "stage-item"}`,
    itemKey,
    jobId: Number.isFinite(numericJobId) && numericJobId > 0 ? numericJobId : null,
    candidateId:
      Number.isFinite(numericCandidateId) && numericCandidateId > 0
        ? numericCandidateId
        : null,
    companyId:
      Number.isFinite(numericCompanyId) && numericCompanyId > 0 ? numericCompanyId : null,
    stageLabel,
    avatar: row?.company_logo || row?.avatar_url || "",
    name: row?.job_title || row?.title || "Job",
    title: employment || companyName || "No company",
    company: companyName || "No company",
    location:
      row?.job_location ||
      row?.location ||
      row?.company_location ||
      row?.candidate_location ||
      "Location unavailable",
    owner: recruiterName || hiringManagerName || "Unassigned",
    ownerLabel: recruiterName
      ? "Recruiter"
      : hiringManagerName
      ? "Hiring Manager"
      : "Owner",
    recruiterProfileId: recruiterName
      ? getRecruiterProfileId(recruiterUsersByName, recruiterName)
      : null,
    leadStatus: stageLabel,
  };
}

function toMovableStageItems(items = []) {
  const seenKeys = new Set();

  return (Array.isArray(items) ? items : []).filter((item) => {
    const itemKey = getStageItemKey(item);
    const jobId = Number(item?.jobId || item?.job_id || 0);
    const candidateId = Number(item?.candidateId || item?.candidate_id || 0);

    if (!itemKey || seenKeys.has(itemKey)) return false;
    if (!Number.isFinite(jobId) || jobId <= 0) return false;
    if (!Number.isFinite(candidateId) || candidateId <= 0) return false;

    seenKeys.add(itemKey);
    return true;
  });
}

function buildListSummary(item = {}) {
  const parts = [];
  const title = String(item?.title || "").trim();
  const company = String(item?.company || "").trim();
  const location = String(item?.location || "").trim();

  if (title && title !== company && title !== "No company") {
    parts.push(title);
  }
  if (company && company !== "No company") {
    parts.push(company);
  }
  if (location && location !== "Location unavailable") {
    parts.push(location);
  }

  return parts.join(" | ") || title || company || location || "-";
}

function StageActionButton({
  children,
  color = PURPLE,
  disabled = false,
  onClick,
}) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        border: 0,
        p: 0,
        m: 0,
        backgroundColor: "transparent",
        fontFamily: "Be Vietnam Pro",
        fontSize: 10.5,
        fontWeight: 900,
        color,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {children}
    </Box>
  );
}

function StageTextLink({ children, onClick, sx = {} }) {
  return (
    <MuiLink
      component="button"
      type="button"
      underline="hover"
      onClick={onClick}
      sx={{
        p: 0,
        border: 0,
        backgroundColor: "transparent",
        color: "inherit",
        fontSize: "inherit",
        fontWeight: "inherit",
        fontFamily: "inherit",
        lineHeight: "inherit",
        textAlign: "left",
        cursor: "pointer",
        textUnderlineOffset: "2px",
        minWidth: 0,
        "&:hover": {
          color: "inherit",
        },
        ...sx,
      }}
    >
      {children}
    </MuiLink>
  );
}

function StageCard({
  item,
  checked = false,
  moving = false,
  onToggleSelect,
  onOpenMoveStageMenu,
  onRejectItem,
  onOpenJob,
  onOpenCompany,
  onOpenRecruiterProfile,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: "#E6EAF2",
        borderRadius: 2,
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Avatar
            src={item.avatar}
            variant="rounded"
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#EEF2FF",
              color: BLUE,
              fontFamily: "Be Vietnam Pro",
              fontWeight: 900,
              fontSize: 12,
              flex: "0 0 auto",
            }}
          >
            {item.name?.[0]}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            {item.jobId ? (
              <StageTextLink
                onClick={() => onOpenJob?.(item.jobId)}
                sx={{
                  display: "block",
                  width: "250px",
                  maxWidth: "100%",
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 11.5,
                  fontWeight: 900,
                  color: BLUE,
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.name}
              </StageTextLink>
            ) : (
              <Typography
                sx={{
                  width: "250px",
                  maxWidth: "100%",
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 11.5,
                  fontWeight: 900,
                  color: BLUE,
                  lineHeight: 1.1,
                }}
                noWrap
              >
                {item.name}
              </Typography>
            )}

            <Typography
              sx={{
                mt: 0.4,
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: MUTED,
              }}
              noWrap
            >
              {item.title}
            </Typography>
          </Box>

          <Checkbox
            size="small"
            checked={checked}
            disabled={!item.itemKey || moving}
            onChange={() => onToggleSelect?.(item.itemKey)}
            onClick={(event) => event.stopPropagation()}
            sx={{
              p: 0,
              width: 18,
              height: 18,
              mt: 0.2,
              "& .MuiSvgIcon-root": { fontSize: 18 },
              color: "#D0D5DD",
              "&.Mui-checked": { color: PURPLE },
            }}
          />
        </Box>

        <Divider sx={{ my: 1, borderColor: "#EEF2F7" }} />

        <Box sx={{ display: "grid", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Building size="14" color={MUTED} variant="Outline" />
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: TEXT,
              }}
              noWrap
            >
              Company:{" "}
              {item.companyId && item.company && item.company !== "No company" ? (
                <StageTextLink
                  onClick={() => onOpenCompany?.(item.companyId, item.company)}
                  sx={{ color: BLUE, fontWeight: 900 }}
                >
                  {item.company}
                </StageTextLink>
              ) : (
                item.company
              )}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Location size="14" color={MUTED} variant="Outline" />
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: TEXT,
              }}
              noWrap
            >
              Location: {item.location}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <ProfileCircle size="14" color={MUTED} variant="Outline" />
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: TEXT,
              }}
              noWrap
            >
              {item.ownerLabel}:{" "}
              {item.ownerLabel === "Recruiter" && item.recruiterProfileId ? (
                <StageTextLink
                  onClick={() => onOpenRecruiterProfile?.(item.recruiterProfileId)}
                  sx={{ color: BLUE, fontWeight: 900 }}
                >
                  {item.owner}
                </StageTextLink>
              ) : (
                item.owner
              )}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Clock size="14" color={MUTED} variant="Outline" />
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: TEXT,
              }}
              noWrap
            >
              Status: {item.leadStatus}
            </Typography>
          </Box>
        </Box>
        <InterviewScheduleInline candidateId={item.candidateId} jobId={item.jobId} stage={item.stageLabel} />

        <Box
          sx={{ mt: 1.1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.1 }}
        >
          <StageActionButton
            disabled={!item.itemKey || moving}
            onClick={(event) => onOpenMoveStageMenu?.(event, item)}
          >
            Move Stage
          </StageActionButton>
          <StageActionButton
            color={MUTED}
            disabled={!item.itemKey || moving}
            onClick={() => onRejectItem?.(item)}
          >
            Reject
          </StageActionButton>
        </Box>
      </Box>
    </Card>
  );
}

function StageRow({
  item,
  checked = false,
  moving = false,
  onToggleSelect,
  onOpenMoveStageMenu,
  onRejectItem,
  onOpenJob,
  onOpenCompany,
  onOpenRecruiterProfile,
}) {
  return (
    <Box
      sx={{
        border: "1px solid #E6EAF2",
        borderRadius: 2,
        backgroundColor: "#fff",
        px: 1.25,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.2,
      }}
    >
      <Avatar
        src={item.avatar}
        variant="rounded"
        sx={{
          width: 28,
          height: 28,
          bgcolor: "#EEF2FF",
          color: BLUE,
          fontFamily: "Be Vietnam Pro",
          fontWeight: 900,
          fontSize: 12,
          flex: "0 0 auto",
        }}
      >
        {item.name?.[0]}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        {item.jobId ? (
          <StageTextLink
            onClick={() => onOpenJob?.(item.jobId)}
            sx={{
              display: "block",
              width: "250px",
              maxWidth: "100%",
              fontFamily: "Be Vietnam Pro",
              fontSize: 11.5,
              fontWeight: 900,
              color: BLUE,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.name}
          </StageTextLink>
        ) : (
          <Typography
            sx={{
              width: "250px",
              maxWidth: "100%",
              fontFamily: "Be Vietnam Pro",
              fontSize: 11.5,
              fontWeight: 900,
              color: BLUE,
              lineHeight: 1.1,
            }}
            noWrap
          >
            {item.name}
          </Typography>
        )}
        <Typography
          sx={{
            mt: 0.35,
            fontFamily: "Be Vietnam Pro",
            fontSize: 10.5,
            fontWeight: 800,
            color: MUTED,
          }}
          noWrap
        >
          {buildListSummary(item)}
        </Typography>

        <Typography
          sx={{
            mt: 0.35,
            fontFamily: "Be Vietnam Pro",
            fontSize: 10,
            fontWeight: 800,
            color: TEXT,
          }}
          noWrap
        >
          Company:{" "}
          {item.companyId && item.company && item.company !== "No company" ? (
            <StageTextLink
              onClick={() => onOpenCompany?.(item.companyId, item.company)}
              sx={{ color: BLUE, fontWeight: 900 }}
            >
              {item.company}
            </StageTextLink>
          ) : (
            item.company
          )}
        </Typography>

        {item.ownerLabel === "Recruiter" ? (
          <Typography
            sx={{
              mt: 0.35,
              fontFamily: "Be Vietnam Pro",
              fontSize: 10,
              fontWeight: 800,
              color: TEXT,
            }}
            noWrap
          >
            Recruiter:{" "}
            {item.recruiterProfileId ? (
              <StageTextLink
                onClick={() => onOpenRecruiterProfile?.(item.recruiterProfileId)}
                sx={{ color: BLUE, fontWeight: 900 }}
              >
                {item.owner}
              </StageTextLink>
            ) : (
              item.owner
            )}
          </Typography>
        ) : null}

        <InterviewScheduleInline candidateId={item.candidateId} jobId={item.jobId} stage={item.stageLabel} />
        <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 1.1, flexWrap: "wrap" }}>
          <StageActionButton
            disabled={!item.itemKey || moving}
            onClick={(event) => onOpenMoveStageMenu?.(event, item)}
          >
            Move Stage
          </StageActionButton>
          <StageActionButton
            color={MUTED}
            disabled={!item.itemKey || moving}
            onClick={() => onRejectItem?.(item)}
          >
            Reject
          </StageActionButton>
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: "Be Vietnam Pro",
          fontSize: 10.5,
          fontWeight: 900,
          color: MUTED,
          whiteSpace: "nowrap",
        }}
      >
        {item.leadStatus}
      </Typography>

      <Checkbox
        size="small"
        checked={checked}
        disabled={!item.itemKey || moving}
        onChange={() => onToggleSelect?.(item.itemKey)}
        sx={{
          p: 0,
          width: 18,
          height: 18,
          "& .MuiSvgIcon-root": { fontSize: 18 },
          color: "#D0D5DD",
          "&.Mui-checked": { color: PURPLE },
        }}
      />
    </Box>
  );
}

export default function CandidateStagesTabContent({
  candidateId: candidateIdProp,
  userId: userIdProp,
  refreshKey,
  onStageDataChanged,
  recruiterUsersByName,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const candidateId = useMemo(() => {
    return (
      toPositiveInt(candidateIdProp) ||
      toPositiveInt(userIdProp) ||
      toPositiveInt(searchParams.get("candidateId")) ||
      toPositiveInt(searchParams.get("userId")) ||
      toPositiveInt(params?.candidateId) ||
      toPositiveInt(params?.userId) ||
      toPositiveInt(params?.id) ||
      null
    );
  }, [candidateIdProp, userIdProp, searchParams, params]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stageActionError, setStageActionError] = useState("");
  const [selectedItemKeys, setSelectedItemKeys] = useState([]);
  const [pendingItemKeys, setPendingItemKeys] = useState([]);
  const [moveStageSaving, setMoveStageSaving] = useState(false);
  const [rejectionItems, setRejectionItems] = useState([]);
  const [moveStageMenuState, setMoveStageMenuState] = useState({
    anchorEl: null,
    items: [],
  });

  const stageOrder = useMemo(() => STAGE_ORDER, []);
  const stageIcons = useMemo(
    () => ({
      New: <AccordionHeadingIcon stage="New" />,
      Applied: <AccordionHeadingIcon stage="Applied" />,
      Matched: <AccordionHeadingIcon stage="Matched" />,
      Sourced: <AccordionHeadingIcon stage="Sourced" />,
      Contacted: <AccordionHeadingIcon stage="Contacted" />,
      Screened: <AccordionHeadingIcon stage="Screened" />,
      Presented: <AccordionHeadingIcon stage="Presented" />,
      Interview: <AccordionHeadingIcon stage="Interview" />,
      Assessment: <AccordionHeadingIcon stage="Assessment" />,
      Offer: <AccordionHeadingIcon stage="Offer" />,
      Hired: <AccordionHeadingIcon stage="Hired" />,
      Rejected: <AccordionHeadingIcon stage="Rejected" />,
    }),
    []
  );

  const stageItems = useMemo(
    () =>
      (Array.isArray(rows) ? rows : []).map((row) =>
        mapRowToStageItem(row, recruiterUsersByName)
      ),
    [recruiterUsersByName, rows]
  );

  const stageItemsByKey = useMemo(() => {
    const nextMap = new Map();
    stageItems.forEach((item) => {
      if (item.itemKey) {
        nextMap.set(item.itemKey, item);
      }
    });
    return nextMap;
  }, [stageItems]);

  const selectedStageItems = useMemo(
    () => selectedItemKeys.map((itemKey) => stageItemsByKey.get(itemKey)).filter(Boolean),
    [selectedItemKeys, stageItemsByKey]
  );
  const selectedItemKeySet = useMemo(() => new Set(selectedItemKeys), [selectedItemKeys]);
  const pendingItemKeySet = useMemo(() => new Set(pendingItemKeys), [pendingItemKeys]);

  const fetchCandidateStages = useCallback(async () => {
    if (!candidateId) {
      setRows([]);
      setError("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data } = await axios.get(buildApiUrl("/candidate-stages"), {
        params: {
          candidateId,
          stage: "all",
          sortBy: "default",
        },
        headers: getAuthHeaders(),
        withCredentials: true,
      });

      const apiGroups = Array.isArray(data?.stages) ? data.stages : [];
      const nextRows = apiGroups.flatMap((group) =>
        Array.isArray(group?.items) ? group.items : []
      );

      setRows(nextRows);
    } catch (err) {
      console.error("Failed to fetch candidate stages:", err);
      setRows([]);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.details ||
          err?.message ||
          "Failed to load candidate stages."
      );
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    fetchCandidateStages();
  }, [fetchCandidateStages, refreshKey]);

  useEffect(() => {
    const validItemKeys = new Set(stageItemsByKey.keys());
    setSelectedItemKeys((prev) => prev.filter((itemKey) => validItemKeys.has(itemKey)));
    setPendingItemKeys((prev) => prev.filter((itemKey) => validItemKeys.has(itemKey)));
  }, [stageItemsByKey]);

  const toggleItemSelection = useCallback((itemKey) => {
    if (!itemKey) return;

    setSelectedItemKeys((prev) =>
      prev.includes(itemKey)
        ? prev.filter((value) => value !== itemKey)
        : [...prev, itemKey]
    );
  }, []);

  const openMoveStageMenu = useCallback(
    (anchorEl, items = []) => {
      if (moveStageSaving) return;

      const normalizedItems = toMovableStageItems(items);
      if (!anchorEl || !normalizedItems.length) return;

      setStageActionError("");
      setMoveStageMenuState({
        anchorEl,
        items: normalizedItems,
      });
    },
    [moveStageSaving]
  );

  const closeMoveStageMenu = useCallback(() => {
    if (moveStageSaving) return;

    setMoveStageMenuState({
      anchorEl: null,
      items: [],
    });
  }, [moveStageSaving]);

  const handleOpenBulkMoveStageMenu = useCallback(
    (event) => {
      openMoveStageMenu(event.currentTarget, selectedStageItems);
    },
    [openMoveStageMenu, selectedStageItems]
  );

  const handleOpenItemMoveStageMenu = useCallback(
    (event, item) => {
      event.preventDefault();
      event.stopPropagation();
      openMoveStageMenu(event.currentTarget, [item]);
    },
    [openMoveStageMenu]
  );

  const handleOpenJob = useCallback(
    (jobId) => {
      const normalizedJobId = Number(jobId || 0);
      if (!Number.isFinite(normalizedJobId) || normalizedJobId <= 0) return;

      navigate({
        pathname: "/jobs",
        search: `?jobId=${encodeURIComponent(String(normalizedJobId))}&tab=details`,
      });
    },
    [navigate]
  );

  const handleOpenCompanyProfile = useCallback(
    (companyId, companyName = "") => {
      const normalizedCompanyId = Number(companyId || 0);
      if (!Number.isFinite(normalizedCompanyId) || normalizedCompanyId <= 0) return;

      const resolvedCompanyName = String(companyName || "").trim() || "Company";
      const payload = buildOpenCompanyNavPayload({
        company: {
          id: normalizedCompanyId,
          company_id: normalizedCompanyId,
          companyId: normalizedCompanyId,
          name: resolvedCompanyName,
          company_name: resolvedCompanyName,
        },
        defaultTab: "details",
        returnListTab: "companies",
        entityType: "company",
      });
      if (!payload) return;

      try {
        sessionStorage.setItem(NAV_CACHE_KEY, JSON.stringify(payload));
      } catch {
        // Ignore session storage failures.
      }

      navigate(
        {
          pathname: "/companies",
          search: `?companyId=${encodeURIComponent(String(normalizedCompanyId))}&tab=details&entityType=company&returnTab=companies`,
        },
        {
          state: payload,
        }
      );
    },
    [navigate]
  );

  const handleOpenRecruiterProfile = useCallback(
    (recruiterProfileId) => {
      const normalizedProfileId = Number(recruiterProfileId || 0);
      if (!Number.isFinite(normalizedProfileId) || normalizedProfileId <= 0) return;

      navigate(`/profile/${encodeURIComponent(String(normalizedProfileId))}`);
    },
    [navigate]
  );

  const executeStageChange = useCallback(
    async (items, nextStage, rejection) => {
      if (moveStageSaving) return;

      const movableItems = toMovableStageItems(items);
      if (!movableItems.length) return;
      if (nextStage === "Rejected" && !rejection) {
        setMoveStageMenuState({ anchorEl: null, items: [] });
        setRejectionItems(movableItems);
        return;
      }

      const groupedItems = movableItems.reduce((acc, item) => {
        const jobId = Number(item?.jobId || 0);
        const itemCandidateId = Number(item?.candidateId || candidateId || 0);
        if (!Number.isFinite(jobId) || jobId <= 0) return acc;
        if (!Number.isFinite(itemCandidateId) || itemCandidateId <= 0) return acc;

        if (!acc.has(jobId)) {
          acc.set(jobId, new Set());
        }

        acc.get(jobId).add(itemCandidateId);
        return acc;
      }, new Map());

      const movedItemKeys = movableItems
        .map((item) => item.itemKey || getStageItemKey(item))
        .filter(Boolean);

      if (!groupedItems.size || !movedItemKeys.length) return;

      setMoveStageSaving(true);
      setPendingItemKeys(movedItemKeys);
      setStageActionError("");

      try {
        await Promise.all(
          Array.from(groupedItems.entries()).map(([jobId, candidateIds]) =>
            axios.patch(
              buildApiUrl(`/jobs/${jobId}/matches/stage`),
              {
                candidate_ids: Array.from(candidateIds),
                stage: nextStage,
                ...rejection,
              },
              {
                headers: getAuthHeaders(),
                withCredentials: true,
              }
            )
          )
        );

        setRejectionItems([]);
        setSelectedItemKeys((prev) =>
          prev.filter((itemKey) => !movedItemKeys.includes(itemKey))
        );
        setMoveStageMenuState({
          anchorEl: null,
          items: [],
        });

        if (typeof onStageDataChanged === "function") {
          await Promise.resolve(onStageDataChanged());
        } else {
          await fetchCandidateStages();
        }
      } catch (err) {
        console.error("Failed to move candidate stage records:", err);
        setStageActionError(
          err?.response?.data?.error ||
            err?.response?.data?.details ||
            err?.message ||
            "Failed to move stage."
        );

        if (typeof onStageDataChanged === "function") {
          await Promise.resolve(onStageDataChanged());
        } else {
          await fetchCandidateStages().catch(() => {});
        }
        if (rejection) throw new Error(err?.response?.data?.error || err?.message || "Unable to save rejection.");
      } finally {
        setMoveStageSaving(false);
        setPendingItemKeys([]);
      }
    },
    [candidateId, fetchCandidateStages, moveStageSaving, onStageDataChanged]
  );

  const handleSelectMoveStage = useCallback(
    async (nextStage, items = moveStageMenuState.items) => {
      await executeStageChange(items, nextStage);
    },
    [executeStageChange, moveStageMenuState.items]
  );

  const handleRejectItem = useCallback(
    async (item) => {
      await executeStageChange([item], "Rejected");
    },
    [executeStageChange]
  );

  const allStages = useMemo(
    () =>
      stageOrder.map((key) => ({
        key,
        icon: stageIcons[key],
        items: stageItems.filter((item) => item.stageLabel === key),
      })),
    [stageIcons, stageItems, stageOrder]
  );

  const stages = allStages;

  const defaultExpanded = useMemo(
    () =>
      stages.reduce((acc, stage) => {
        acc[stage.key] = stage.items.length > 0;
        return acc;
      }, {}),
    [stages]
  );

  return (
    <Box sx={{ backgroundColor: BG }}>
      <RejectedStageDialog jobSpecific open={rejectionItems.length > 0} loading={moveStageSaving} candidateCount={rejectionItems.length}
        onClose={() => setRejectionItems([])} onSubmit={(rejection) => executeStageChange(rejectionItems, "Rejected", rejection)} />
      <Box sx={{ pt: 3, pb: 3 }}>
        <Button
          variant="contained"
          startIcon={<MoveStageActionIcon size={16} />}
          endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />}
          disabled={moveStageSaving || !selectedItemKeys.length}
          sx={MOVE_STAGE_BULK_BUTTON_SX}
          onClick={handleOpenBulkMoveStageMenu}
        >
          Move Stage
        </Button>
      </Box>

      {stageActionError ? (
        <Box sx={{ pb: 2 }}>
          <Alert severity="error">{stageActionError}</Alert>
        </Box>
      ) : null}

      {!candidateId ? (
        <Box sx={{ pb: 3 }}>
          <Alert severity="info">
            Candidate id is missing. Checked props, query params, and route params.
          </Alert>
        </Box>
      ) : loading ? (
        <Box sx={{ py: 4, display: "grid", placeItems: "center" }}>
          <CircularProgress size={26} />
        </Box>
      ) : error ? (
        <Box sx={{ pb: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : (
        <RecruitingStageAccordionList
          candidateId={candidateId}
          refreshKey={refreshKey}
          pageSize={10}
          stages={stages}
          defaultExpanded={defaultExpanded}
          defaultView="grid"
          emptyText="No applications yet."
          renderGrid={({ items }) => (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1.25,
              }}
            >
              {items.map((item) => (
                <StageCard
                  key={item.id}
                  item={item}
                  checked={!!(item.itemKey && selectedItemKeySet.has(item.itemKey))}
                  moving={!!(item.itemKey && pendingItemKeySet.has(item.itemKey))}
                  onToggleSelect={toggleItemSelection}
                  onOpenMoveStageMenu={handleOpenItemMoveStageMenu}
                  onRejectItem={handleRejectItem}
                  onOpenJob={handleOpenJob}
                  onOpenCompany={handleOpenCompanyProfile}
                  onOpenRecruiterProfile={handleOpenRecruiterProfile}
                />
              ))}
            </Box>
          )}
          renderList={({ items }) => (
            <Box sx={{ display: "grid", gap: 1 }}>
              {items.map((item) => (
                <StageRow
                  key={item.id}
                  item={item}
                  checked={!!(item.itemKey && selectedItemKeySet.has(item.itemKey))}
                  moving={!!(item.itemKey && pendingItemKeySet.has(item.itemKey))}
                  onToggleSelect={toggleItemSelection}
                  onOpenMoveStageMenu={handleOpenItemMoveStageMenu}
                  onRejectItem={handleRejectItem}
                  onOpenJob={handleOpenJob}
                  onOpenCompany={handleOpenCompanyProfile}
                  onOpenRecruiterProfile={handleOpenRecruiterProfile}
                />
              ))}
            </Box>
          )}
        />
      )}

      <Menu
        anchorEl={moveStageMenuState.anchorEl}
        open={Boolean(moveStageMenuState.anchorEl)}
        onClose={closeMoveStageMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
      >
        {MOVE_STAGE_OPTIONS.map((stageOption) => (
          <MenuItem
            key={stageOption}
            disabled={moveStageSaving}
            onClick={() => handleSelectMoveStage(stageOption)}
            sx={{
              fontFamily: "Be Vietnam Pro",
              fontSize: 12,
              fontWeight: 800,
              color: TEXT,
            }}
          >
            {stageOption}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
